import React, { useEffect, useRef } from "react";
import { gsap } from "gsap";
import { ScrollTrigger } from "gsap/ScrollTrigger";
import { motion } from "framer-motion";
import { 
  Store, 
  BarChart2, 
  Calendar, 
  Brush as PaintBrush, 
  Globe, 
  Users, 
  Leaf 
} from "lucide-react";
import { 
  fadeInUp, 
  staggerContainer, 
  fadeIn, 
  scaleIn 
} from '../utils/animations';

// Hero Section
const HeroSection = () => {
    const sectionRef = useRef(null);
    const triggerRef = useRef(null);
    
    useEffect(() => {
      gsap.registerPlugin(ScrollTrigger);
      
      const section = sectionRef.current;
      
      const scrollTrigger = ScrollTrigger.create({
        trigger: triggerRef.current,
        start: "top 80%", 
        end: "bottom 20%", 
        toggleActions: "play reverse play reverse",
        onEnter: () => {
          gsap.to(section, { 
            opacity: 1, 
            y: 0, 
            duration: 0.5,
            ease: "power1.out" 
          });
        },
        onLeave: () => {
          gsap.to(section, { 
            opacity: 0.3, 
            y: 50, 
            duration: 0.5,
            ease: "power1.in" 
          });
        },
        onEnterBack: () => {
          gsap.to(section, { 
            opacity: 1, 
            y: 0, 
            duration: 0.5,
            ease: "power1.out" 
          });
        },
        onLeaveBack: () => {
          gsap.to(section, { 
            opacity: 0.3, 
            y: -50, 
            duration: 0.5,
            ease: "power1.in" 
          });
        }
      });

      // Additional GSAP animations
      const ctx = gsap.context(() => {
        // Split text animation for each letter
        const chars = section.querySelectorAll('.hero-title span');
        
        gsap.from(chars, {
          opacity: 0,
          y: 100,
          rotateX: -90,
          stagger: 0.02,
          duration: 1,
          ease: "back.out(1.7)"
        });
  
        // Subtitle animation
        gsap.from(".hero-subtitle", {
          opacity: 0,
          y: 50,
          duration: 1,
          delay: 0.5,
          ease: "power3.out"
        });
  
        // Floating cards animation
        gsap.to(".feature-card", {
          y: "random(-20, 20)",
          rotation: "random(-5, 5)",
          duration: "random(2, 3)",
          ease: "power1.inOut",
          yoyo: true,
          repeat: -1,
          stagger: {
            amount: 1,
            from: "random"
          }
        });
  
        // Background parallax
        gsap.to(".parallax-bg", {
          scrollTrigger: {
            trigger: section,
            start: "top top",
            end: "bottom top",
            scrub: 1
          },
          y: "30%"
        });
  
        // Particle animations
        gsap.to(".particle", {
          x: "random(-100, 100)",
          y: "random(-100, 100)",
          rotation: "random(-360, 360)",
          duration: "random(3, 6)",
          repeat: -1,
          yoyo: true,
          ease: "none",
          stagger: {
            amount: 2,
            from: "random"
          }
        });
      }, section);

      return () => {
        scrollTrigger.kill();
        ctx.revert();
      };
    }, []);
  
    const title = "Jansdell Bharathi Foundation".split("").map((char, i) => (
      <motion.span
        key={i}
        className="inline-block hero-title font-serif italic text-amber-900"
        variants={fadeIn}
        initial="hidden"
        animate="visible"
        transition={{ 
          delay: i * 0.03,
          duration: 0.6
        }}
      >
        {char === " " ? "\u00A0" : char}
      </motion.span>
    ));
    return (
      <div ref={triggerRef}>
        <motion.section 
          ref={sectionRef}
          initial={{ opacity: 0, y: 50 }}
          className="relative min-h-screen flex items-center justify-center overflow-hidden pt-20"
          style={{ opacity: 0 }}
        >
          {/* Parallax Background */}
          <div className="parallax-bg absolute inset-0 bg-gradient-to-br from-amber-50 via-white to-amber-100" />
          
          {/* Floating Particles */}
          <div className="absolute inset-0 pointer-events-none">
            {[...Array(15)].map((_, i) => (
              <div 
                key={i} 
                className="particle absolute w-3 h-3 bg-amber-200/30 rounded-full"
                style={{
                  left: `${Math.random() * 100}%`,
                  top: `${Math.random() * 100}%`
                }}
              />
            ))}
          </div>
    
          <div className="container mx-auto px-4 relative z-10">
            <div className="text-center max-w-4xl mx-auto mb-16">
              <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold text-gray-900 mb-8 leading-tight">
                {title}
              </h1>
              <motion.p 
                className="hero-subtitle text-lg md:text-xl lg:text-2xl text-gray-600 mb-10"
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.5 }}
              >
                A dynamic, multidisciplinary research platform committed to driving 
                innovative solutions and positive change across global challenges.
              </motion.p>
            </div>
    
            <motion.div
              variants={staggerContainer}
              initial="hidden"
              whileInView="visible"
              viewport={{ once: true }}
              className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 md:gap-8"
            >
              <FeatureCard 
                icon={<Store />}
                title="Sustainable Development"
                description="Pioneering strategies for environmental and economic sustainability"
              />
              <FeatureCard 
                icon={<BarChart2 />}
                title="Ethical AI Advocacy"
                description="Championing responsible and inclusive artificial intelligence"
              />
              <FeatureCard 
                icon={<Calendar />}
                title="Social Impact"
                description="Empowering communities through targeted research initiatives"
              />
              <FeatureCard 
                icon={<PaintBrush />}
                title="Cultural Preservation"
                description="Protecting and celebrating diverse cultural heritage"
              />
            </motion.div>
    
            <motion.div 
              className="text-center mt-12 md:mt-16"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ delay: 1.5 }}
            >
              <Button href="focus.html">Explore Our Vision</Button>
            </motion.div>

            {/* Additional Information Section */}
            <motion.div
              variants={staggerContainer}
              initial="hidden"
              whileInView="visible"
              viewport={{ once: true }}
              className="mt-16 md:mt-24 bg-white/80 backdrop-blur-sm rounded-2xl p-8 md:p-12 shadow-xl"
            >
              <div className="grid grid-cols-1 md:grid-cols-3 gap-6 md:gap-10">
                <motion.div variants={fadeInUp} className="text-center">
                  <div className="flex justify-center mb-4">
                    <Globe className="w-12 h-12 text-amber-500" />
                  </div>
                  <h3 className="text-xl font-semibold mb-2">Global Perspective</h3>
                  <p className="text-gray-600">
                    Addressing complex challenges with a comprehensive, international approach
                  </p>
                </motion.div>
                <motion.div variants={fadeInUp} className="text-center">
                  <div className="flex justify-center mb-4">
                    <Users className="w-12 h-12 text-amber-500" />
                  </div>
                  <h3 className="text-xl font-semibold mb-2">Collaborative Research</h3>
                  <p className="text-gray-600">
                    Bringing together diverse minds to generate innovative solutions
                  </p>
                </motion.div>
                <motion.div variants={fadeInUp} className="text-center">
                  <div className="flex justify-center mb-4">
                    <Leaf className="w-12 h-12 text-amber-500" />
                  </div>
                  <h3 className="text-xl font-semibold mb-2">Transformative Impact</h3>
                  <p className="text-gray-600">
                    Creating meaningful change through cutting-edge research and action
                  </p>
                </motion.div>
              </div>
            </motion.div>
          </div>
        </motion.section>
      </div>
    );
  };
  
  // Feature Card Component
  const FeatureCard = ({ icon, title, description }) => (
    <motion.div
      className="feature-card bg-white/80 backdrop-blur-sm p-6 md:p-8 rounded-xl shadow-lg"
      variants={scaleIn}
      whileHover={{ 
        scale: 1.05,
        boxShadow: "0 20px 25px -5px rgba(0, 0, 0, 0.1)"
      }}
      transition={{ type: "spring", stiffness: 300 }}
    >
      <motion.div 
        className="text-amber-500 mb-4"
        whileHover={{ rotate: 360 }}
        transition={{ duration: 0.6 }}
      >
        {React.cloneElement(icon, { size: 48 })}
      </motion.div>
      <h3 className="text-lg md:text-xl font-semibold text-gray-800 mb-2">{title}</h3>
      <p className="text-gray-600 text-sm md:text-base">{description}</p>
    </motion.div>
  );
  
  // Reusable Button Component
  const Button = ({ href, children }) => (
    <motion.a
      href={href}
      className="inline-block bg-amber-500 text-white px-8 py-3 rounded-lg shadow-lg hover:bg-amber-600 transition-all duration-300"
      whileHover={{ scale: 1.05 }}
      whileTap={{ scale: 0.95 }}
    >
      {children}
    </motion.a>
  );

export default HeroSection;